import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });

  if (user) return <Navigate to="/" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm.name, registerForm.email, registerForm.password);
      toast.success("Compte créé avec succès");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err, "Inscription impossible"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div className="flex items-center gap-3">
          <span className="bg-white text-slate-900 p-2 rounded-lg">
            <FileSignature className="h-6 w-6" />
          </span>
          <span className="font-extrabold tracking-tight text-xl">ContratExpress Pro</span>
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl font-extrabold tracking-tight leading-tight">
            Générez vos contrats en quelques secondes.
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed max-w-md">
            Photographiez une pièce d'identité, vérifiez le nom, choisissez le modèle :
            le contrat est généré, sauvegardé et prêt à imprimer.
          </p>
          <ul className="space-y-3 text-slate-300 text-sm">
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Lecture automatique par OCR</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Modèles Word officiels préservés</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Export PDF &amp; Word, impression directe</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Archivage sécurisé et recherche instantanée</li>
          </ul>
        </div>
        <p className="text-xs text-slate-500">Données chiffrées et accès protégé.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <span className="bg-slate-900 text-white p-2 rounded-lg">
              <FileSignature className="h-5 w-5" />
            </span>
            <span className="font-extrabold tracking-tight text-slate-900 text-lg">ContratExpress Pro</span>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Bienvenue</h2>
          <p className="text-sm text-slate-500 mb-6">Connectez-vous pour accéder à vos contrats.</p>

          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" required data-testid="auth-login-email-input"
                    value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    placeholder="vous@exemple.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <Input id="login-password" type="password" required data-testid="auth-login-password-input"
                    value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="••••••••" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="auth-login-submit-button">
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Se connecter
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name">Nom complet</Label>
                  <Input id="register-name" required data-testid="auth-register-name-input"
                    value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    placeholder="Votre nom" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email">Email</Label>
                  <Input id="register-email" type="email" required data-testid="auth-register-email-input"
                    value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    placeholder="vous@exemple.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password">Mot de passe (8 caractères min.)</Label>
                  <Input id="register-password" type="password" required minLength={8} data-testid="auth-register-password-input"
                    value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    placeholder="••••••••" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="auth-register-submit-button">
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Créer mon compte
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">ou</span></div>
          </div>

          <Button variant="outline" className="w-full" onClick={handleGoogle} data-testid="auth-google-button">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continuer avec Google
          </Button>
        </div>
      </div>
    </div>
  );
}
